<template>
  <div class="payment-history">
    <DataTable 
      :value="orders" 
      :paginator="orders.length > 10" 
      :rows="10"
      :rowsPerPageOptions="[10, 25, 50]"
      sortField="created_at"
      :sortOrder="-1"
      class="payment-history-table"
      :expandedRows="expandedRows"
      @row-click="onRowClick"
      dataKey="id"
    >
      <template #empty>
        <div class="empty-state">
          <i class="pi pi-inbox" style="font-size: 2rem; color: #9ca3af;"></i>
          <p>No hay pagos registrados</p>
        </div>
      </template>

      <Column field="created_at" header="Fecha" sortable>
        <template #body="{ data }">
          {{ formatDate(data.created_at) }}
        </template>
      </Column>

      <Column field="payment_type" header="Tipo" sortable>
        <template #body="{ data }">
          <div class="payment-type">
            <i :class="getPaymentTypeIcon(data.payment_type)"></i>
            <span>{{ getPaymentTypeLabel(data.payment_type) }}</span>
          </div>
        </template>
      </Column>

      <Column field="description" header="Descripción">
        <template #body="{ data }">
          {{ data.description }}
        </template>
      </Column>

      <Column field="amount_cop" header="Monto" sortable>
        <template #body="{ data }">
          {{ formatAmount(data.amount_cop) }}
        </template>
      </Column>

      <Column field="status" header="Estado" sortable>
        <template #body="{ data }">
          <Tag 
            :value="getStatusLabel(data.status)" 
            :severity="getStatusSeverity(data.status)"
          />
        </template>
      </Column>

      <Column header="Acciones" style="width: 200px;">
        <template #body="{ data }">
          <div class="action-buttons">
            <!-- Pending orders: show "Pagar ahora" button -->
            <Button 
              v-if="data.status === 'pending' && data.payment_link_url"
              label="Pagar ahora"
              icon="pi pi-external-link"
              size="small"
              @click.stop="openPaymentLink(data.payment_link_url)"
            />
            
            <!-- Expired/Failed orders (admin only): show "Regenerar enlace" button -->
            <Button 
              v-if="isAdmin && (data.status === 'expired' || data.status === 'failed')"
              label="Regenerar enlace"
              icon="pi pi-refresh"
              size="small"
              severity="secondary"
              @click.stop="regenerateLink(data.id)"
              :loading="regenerating && regeneratingOrderId === data.id"
            />
          </div>
        </template>
      </Column>

      <!-- Expandable row for paid orders to show transaction reference -->
      <template #expansion="{ data }">
        <div v-if="data.status === 'paid'" class="order-details">
          <div class="detail-row">
            <strong>Referencia de transacción:</strong>
            <span>{{ data.epayco_transaction_id || 'N/A' }}</span>
          </div>
          <div class="detail-row" v-if="data.paid_at">
            <strong>Fecha de pago:</strong>
            <span>{{ formatDate(data.paid_at) }}</span>
          </div>
        </div>
      </template>
    </DataTable>
  </div>
</template>

<script setup>
import { ref } from 'vue';
import { useToast } from 'primevue/usetoast';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import Tag from 'primevue/tag';
import Button from 'primevue/button';
import api from '../../services/api';

const props = defineProps({
  orders: {
    type: Array,
    default: () => []
  },
  isAdmin: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(['link-regenerated']);

const toast = useToast();
const expandedRows = ref([]);
const regenerating = ref(false);
const regeneratingOrderId = ref(null);

// Format date to human-readable format
const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleDateString('es-CO', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

// Format amount in COP with $ and thousands separator
const formatAmount = (amountCentavos) => {
  const amount = amountCentavos / 100;
  return `$${amount.toLocaleString('es-CO', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} COP`;
};

// Get payment type icon
const getPaymentTypeIcon = (type) => {
  const icons = {
    subscription: 'pi pi-calendar',
    token_package: 'pi pi-box',
    custom: 'pi pi-dollar'
  };
  return icons[type] || 'pi pi-circle';
};

// Get human-readable payment type label
const getPaymentTypeLabel = (type) => {
  const labels = {
    subscription: 'Suscripción',
    token_package: 'Paquete de Tokens',
    custom: 'Pago Personalizado'
  };
  return labels[type] || type;
};

// Get status label
const getStatusLabel = (status) => {
  const labels = {
    pending: 'Pendiente',
    paid: 'Pagado',
    failed: 'Fallido',
    expired: 'Expirado'
  };
  return labels[status] || status;
};

// Get status severity for PrimeVue Tag
const getStatusSeverity = (status) => {
  const severities = {
    pending: 'warning',
    paid: 'success',
    failed: 'danger',
    expired: 'secondary'
  };
  return severities[status] || 'info';
};

// Open payment link in new window
const openPaymentLink = (url) => {
  if (url) {
    window.open(url, '_blank');
  }
};

// Regenerate payment link (admin only)
const regenerateLink = async (orderId) => {
  regenerating.value = true;
  regeneratingOrderId.value = orderId;
  
  try {
    const response = await api.post(`/payments/orders/${orderId}/regenerate`);
    
    toast.add({
      severity: 'success',
      summary: 'Enlace Regenerado',
      detail: 'El enlace de pago ha sido regenerado exitosamente',
      life: 5000
    });
    
    // Emit event to parent to refresh orders
    emit('link-regenerated', response.data);
  } catch (error) {
    console.error('Error regenerating link:', error);
    const errorMessage = error.response?.data?.detail || 'Error al regenerar el enlace de pago';
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: errorMessage,
      life: 5000
    });
  } finally {
    regenerating.value = false;
    regeneratingOrderId.value = null;
  }
};

// Handle row click to expand paid orders
const onRowClick = (event) => {
  const order = event.data;
  
  // Only expand paid orders to show transaction details
  if (order.status === 'paid') {
    const index = expandedRows.value.findIndex(row => row.id === order.id);
    if (index === -1) {
      expandedRows.value.push(order);
    } else {
      expandedRows.value.splice(index, 1);
    }
  }
};
</script>

<style scoped>
.payment-history {
  margin-top: 1rem;
}

.payment-history-table {
  margin-bottom: 1rem;
}

.empty-state {
  text-align: center;
  padding: 3rem;
  color: #9ca3af;
}

.empty-state p {
  margin-top: 0.5rem;
  font-size: 0.95rem;
}

.payment-type {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.payment-type i {
  color: #6366f1;
}

.action-buttons {
  display: flex;
  gap: 0.5rem;
  justify-content: flex-start;
}

.order-details {
  padding: 1rem 2rem;
  background-color: #f9fafb;
  border-left: 3px solid #10b981;
}

.detail-row {
  display: flex;
  gap: 1rem;
  padding: 0.5rem 0;
}

.detail-row strong {
  min-width: 180px;
  color: #374151;
}

.detail-row span {
  color: #6b7280;
}

/* Make paid rows clickable */
:deep(.p-datatable-tbody > tr[data-p-selectable-row]) {
  cursor: pointer;
}

:deep(.p-datatable-tbody > tr:hover) {
  background-color: #f9fafb;
}
</style>
